import { useCallback, useContext } from 'react';

import { SceneCanvasContext } from './SceneCanvasContext';
import { Character } from './types';
import { useGetMousePositionFromEvent } from './useMousePositionFromEvent';

export const useBBoxCornerScaling = (characterId: Character['id']) => {
  const { getCharacterById } = useContext(SceneCanvasContext);
  const { position, width, height, ratio } = getCharacterById(characterId);

  const getMousePositionFromEvent = useGetMousePositionFromEvent();

  const scaleFromTopLeft = useCallback(
    (
      event: MouseEvent,
      scalingStartMouseX: number,
      scalingStartMouseY: number,
      callback: (
        newX: number | null,
        newY: number | null,
        newWidth: number | null,
        newHeight: number | null
      ) => void
    ) => {
      const { x: mouseX, y: mouseY } = getMousePositionFromEvent(event);

      const { diffX, diffY } = getMousePositionDifference(
        scalingStartMouseX,
        scalingStartMouseY,
        mouseX,
        mouseY
      );

      // scale by width
      if (Math.abs(diffX) > Math.abs(diffY)) {
        const newWidth = Math.max(width - diffX, 0);
        const newHeightAccordingToRatio = Math.max(newWidth / ratio, 0);
        const newX = position.x + diffX;
        const newY = position.y + diffX / ratio;

        callback(newX, newY, newWidth, newHeightAccordingToRatio);
      }
      // scale by height
      else {
        const newHeight = Math.max(height - diffY, 0);
        const newWidthAccordingToRatio = Math.max(newHeight * ratio, 0);
        const newX = position.x + diffY * ratio;
        const newY = position.y + diffY;

        callback(newX, newY, newWidthAccordingToRatio, newHeight);
      }
    },
    [getMousePositionFromEvent, height, position.x, position.y, ratio, width]
  );
  const scaleFromTopRight = useCallback(
    (
      event: MouseEvent,
      scalingStartMouseX: number,
      scalingStartMouseY: number,
      callback: (
        newX: number | null,
        newY: number | null,
        newWidth: number | null,
        newHeight: number | null
      ) => void
    ) => {
      const { x: mouseX, y: mouseY } = getMousePositionFromEvent(event);

      const { diffX, diffY } = getMousePositionDifference(
        scalingStartMouseX,
        scalingStartMouseY,
        mouseX,
        mouseY
      );

      // scale by width
      if (Math.abs(diffX) > Math.abs(diffY)) {
        const newWidth = Math.max(width + diffX, 0);
        const newHeightAccordingToRatio = Math.max(newWidth / ratio, 0);
        const newY = position.y - diffX / ratio;
        if (newWidth === 0 || newHeightAccordingToRatio === 0) {
          return;
        }
        callback(null, newY, newWidth, newHeightAccordingToRatio);
      }
      // scale by height
      else {
        const newHeight = Math.max(height - diffY, 0);
        const newWidthAccordingToRatio = Math.max(newHeight * ratio, 0);
        const newY = position.y + diffY;

        if (newWidthAccordingToRatio === 0 || newHeight === 0) {
          return;
        }
        callback(null, newY, newWidthAccordingToRatio, newHeight);
      }
    },
    [getMousePositionFromEvent, height, position.y, ratio, width]
  );
  const scaleFromBottomLeft = useCallback(
    (
      event: MouseEvent,
      scalingStartMouseX: number,
      scalingStartMouseY: number,
      callback: (
        newX: number | null,
        newY: number | null,
        newWidth: number | null,
        newHeight: number | null
      ) => void
    ) => {
      const { x: mouseX, y: mouseY } = getMousePositionFromEvent(event);

      const { diffX, diffY } = getMousePositionDifference(
        scalingStartMouseX,
        scalingStartMouseY,
        mouseX,
        mouseY
      );

      // scale by width
      if (Math.abs(diffX) > Math.abs(diffY)) {
        const newWidth = Math.max(width - diffX, 0);
        const newHeightAccordingToRatio = Math.max(newWidth / ratio, 0);
        const newX = position.x + diffX;
        if (newWidth === 0 || newHeightAccordingToRatio === 0) {
          return;
        }
        callback(newX, null, newWidth, newHeightAccordingToRatio);
      }
      // scale by height
      else {
        const newHeight = Math.max(height + diffY, 0);
        const newWidthAccordingToRatio = Math.max(newHeight * ratio, 0);
        const newX = position.x - diffY * ratio;
        if (newWidthAccordingToRatio === 0 || newHeight === 0) {
          return;
        }
        callback(newX, null, newWidthAccordingToRatio, newHeight);
      }
    },
    [getMousePositionFromEvent, height, position.x, ratio, width]
  );
  const scaleFromBottomRight = useCallback(
    (
      event: MouseEvent,
      scalingStartMouseX: number,
      scalingStartMouseY: number,
      callback: (
        newX: number | null,
        newY: number | null,
        newWidth: number | null,
        newHeight: number | null
      ) => void
    ) => {
      const { x: mouseX, y: mouseY } = getMousePositionFromEvent(event);

      const { diffX, diffY } = getMousePositionDifference(
        scalingStartMouseX,
        scalingStartMouseY,
        mouseX,
        mouseY
      );

      // scale by width
      if (diffX > diffY) {
        const newWidth = Math.max(width + diffX, 0);
        const newHeightAccordingToRatio = Math.max(newWidth / ratio, 0);
        if (newWidth === 0 || newHeightAccordingToRatio === 0) {
          return;
        }
        callback(null, null, newWidth, newHeightAccordingToRatio);
      }
      // scale by height
      else {
        const newHeight = Math.max(height + diffY, 0);
        const newWidthAccordingToRatio = Math.max(newHeight * ratio, 0);
        if (newWidthAccordingToRatio === 0 || newHeight === 0) {
          return;
        }
        callback(null, null, newWidthAccordingToRatio, newHeight);
      }
    },
    [getMousePositionFromEvent, height, ratio, width]
  );

  return {
    scaleFromTopLeft,
    scaleFromTopRight,
    scaleFromBottomLeft,
    scaleFromBottomRight,
  };
};

const getMousePositionDifference = (
  scalingStartMouseX: number,
  scalingStartMouseY: number,
  mouseX: number,
  mouseY: number
) => {
  const diffX = mouseX - scalingStartMouseX;
  const diffY = mouseY - scalingStartMouseY;

  return { diffX, diffY };
};
